import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useEventPublicPaths, useEventRouteParams } from '../../hooks/useEventRouteParams';
import type { EventArchiveData } from '../../types/event';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

function placementLabel(placement: string | null): string {
  if (!placement) return 'Award';
  const key = placement.toLowerCase().trim();
  if (key === '1st' || key === 'first') return '1st';
  if (key === '2nd' || key === 'second') return '2nd';
  if (key === '3rd' || key === 'third') return '3rd';
  return placement;
}

const EventArchiveSkeleton: React.FC = () => (
  <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col animate-pulse">
    <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
      <div className="h-4 w-24 bg-[#141414] rounded mb-8" />
      <div className="h-3 w-20 bg-[#141414] rounded mb-3" />
      <div className="h-10 w-3/4 bg-[#141414] rounded mb-10" />
      <div className="space-y-3 mb-10">
        <div className="h-14 bg-[#0c0c0c]/70 rounded-xl border border-white/[0.06]" />
        <div className="h-14 bg-[#0c0c0c]/70 rounded-xl border border-white/[0.06]" />
      </div>
    </div>
  </div>
);

const EventArchivePage: React.FC = () => {
  const { apiQuery } = useEventRouteParams();
  const [data, setData] = useState<EventArchiveData | null>(null);
  const paths = useEventPublicPaths(data?.event ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiQuery) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const archive = await apiService.getEventArchive(apiQuery);
        setData(archive);
        document.title = `${archive.event.title} Archive · Bilt:Spec`;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Archive not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiQuery]);

  if (loading) {
    return <EventArchiveSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <p className="kicker mb-3">Event archive</p>
            <h1 className="heading-section mb-2">Archive not available</h1>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              {error || 'This event archive could not be loaded.'}
            </p>
            <Link to="/events">
              <Button variant="primary" className="min-h-[44px]">Browse events</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { event, classes, winners_by_class, participants } = data;
  const classNames = Object.keys(winners_by_class);
  const hasWinners = classNames.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        <PageBackLink label={`Back to ${event.title}`} to={paths.base} className="mb-6" />

        <header className="mb-10 pb-8 border-b border-white/[0.06]">
          <p className="kicker mb-3">Official record</p>
          <h1 className="heading-display leading-tight">{event.title}</h1>
          <p className="text-muted mt-3 text-sm leading-relaxed">
            Permanent archive: winners and participating builds
          </p>
        </header>

        {classes.length > 0 && (
          <section className="mb-10">
            <h2 className="kicker mb-4">Classes</h2>
            <ul className="divide-y divide-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
              {classes.map((c) => (
                <li key={c.id} className="px-4 py-3.5 bg-[#0c0c0c]/50">
                  <p className="font-medium text-white">{c.name}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mb-10">
          <div className="border-b border-white/[0.06] pb-3 mb-5">
            <p className="kicker mb-2">Verified results</p>
            <h2 className="heading-section">Winners</h2>
          </div>
          {!hasWinners ? (
            <div className="rounded-xl border border-dashed border-white/[0.06] py-12 text-center">
              <p className="text-gray-500 text-sm">Awards appear here once published by the organizer.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {classNames.map((className) => (
                <div key={className}>
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{className}</h3>
                  <ul className="space-y-3">
                    {winners_by_class[className].map((winner, idx) => (
                      <li
                        key={`${className}-${idx}`}
                        className="rounded-xl border border-amber-900/25 bg-amber-950/15 px-4 py-4"
                      >
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-600/90 border border-amber-800/40 rounded px-2 py-1 bg-amber-950/40 mt-0.5">
                            {placementLabel(winner.placement)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-amber-50">{winner.title}</p>
                            <Link
                              to={winner.profile_url}
                              className="block mt-2 group"
                            >
                              <p className="text-white font-semibold group-hover:text-gray-200 transition-colors">
                                {winner.vehicle_label}
                              </p>
                              <p className="text-sm text-gray-500 mt-0.5">
                                {winner.owner_name || winner.owner_username}
                              </p>
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="kicker mb-4">Participating builds</h2>
          {participants.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/[0.06] py-12 text-center">
              <p className="text-gray-500 text-sm">No public builds registered for this event.</p>
            </div>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {participants.map((p) => (
                <li key={p.profile_url}>
                  <Link
                    to={p.profile_url}
                    className="block rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 px-4 py-3.5 hover:border-white/[0.12] hover:bg-[#0c0c0c]/70 transition-colors min-h-[44px]"
                  >
                    <p className="font-medium text-white text-sm">{p.vehicle_label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">@{p.owner_username}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default EventArchivePage;
